import { useOrchestratorApi } from '@/api/orchestrator'
import type { PostWorkflow, TokenDecoded, Mode } from '@/types'
import decode from 'jwt-decode'
import { readonly, ref } from 'vue'

const token = ref<string>()
const decodedToken = ref<TokenDecoded>()
const workflowId = ref<string>()
const partyId = ref<string>()
const mode = ref<Mode>('staging')

export const useWorkspace = () => {

    const validateToken = async (token: string) => {
        const { data } = await useOrchestratorApi().post<never>(
            '/token/validate',
            { token },
        )

        return data
    }

    const getCurrentWorkflow = async (): Promise<PostWorkflow | undefined> => {
        const { data } = await useOrchestratorApi().post<PostWorkflow | undefined>(
            '/workflows',
            null,
            {
                headers: {
                    Authorization: `Bearer ${token.value}`,
                },
            },
        )

        if (data) {
            setWorkflowId(data.id)
            setPartyId(data.caseId)
        }

        return data
    }

    const startWorkflow = async (version?: number): Promise<void> => {
        if (version && version >= 3) {
            await useOrchestratorApi().post(
                `/workflows/${workflowId.value}/run`,
                {
                    Authorization: `Bearer ${token.value}`,
                },
            )
        } else {
            await useOrchestratorApi().put(
                `/workflows/${workflowId.value}`,
                {
                    Authorization: `Bearer ${token.value}`,
                },
            )
        }
    }

    const setToken = (value?: string): TokenDecoded => {
        decodedToken.value = decode<TokenDecoded>(value ?? '')
        token.value = value
        const { environment, partyId } = decodedToken.value
        setMode(environment)
        setPartyId(partyId)
        if (decodedToken.value.workflowId) {
            setWorkflowId(decodedToken.value.workflowId)
        }

        return decodedToken.value
    }

    const clearToken = async () => {
        token.value = undefined
        decodedToken.value = undefined
    }

    const setWorkflowId = (value: string) => {
        workflowId.value = value
    }

    const setPartyId = (value: string) => {
        partyId.value = value
    }

    const setMode = (value: Mode) => {
        mode.value = value
    }

    return {
        validateToken,
        token: readonly(token),
        decodedToken: readonly(decodedToken),
        workflowId: readonly(workflowId),
        mode: readonly(mode),
        setToken,
        setWorkflowId,
        setMode,
        getCurrentWorkflow,
        startWorkflow,
        clearToken, // used in tests
    }
}
